import { useEffect, useState } from "react"

import { useLazyGetAlertStatusesQuery } from "store/api/widgetsApi"
import { ApiTypes } from "types"

import WidgetsLayout from "layouts/WidgetsLayout"
import { BarChart } from "components/common/BarChart"
import { dummyAlertsStatusesWidgetData } from "pages/contractor/AlertsPage/dummyAlertsData"

import styles from "./index.module.scss"

interface AlertStatusesWidgetProps {
  hasAlertsAccess?: boolean
  unsupported?: boolean
  updateIndex: number
  dateRange?: [string, string]
}

const initialWidgetStatusesData: ApiTypes.Res.AlertStatusesWidget = {
  active: 0,
  frozen: 0,
  acknowledged: 0,
  alerts_count: 0,
}

export const WidgetAlertStatuses = ({
  dateRange,
  hasAlertsAccess = true,
  unsupported: unsupportedProp,
  updateIndex,
}: AlertStatusesWidgetProps) => {
  const unsupported = unsupportedProp ?? !hasAlertsAccess

  const [widgetData, setWidgetData] = useState(initialWidgetStatusesData)
  const [triggerGetAlertStatuses, { isFetching }] = useLazyGetAlertStatusesQuery()

  useEffect(() => {
    if (unsupported) {
      setWidgetData(dummyAlertsStatusesWidgetData)
      return
    }

    const fetchData = async () => {
      try {
        const res = await triggerGetAlertStatuses({ date_start: dateRange?.[0], date_end: dateRange?.[1] })
        if (res?.data?.data) {
          setWidgetData({
            active: res.data.data.active ?? 0,
            frozen: res.data.data.frozen ?? 0,
            acknowledged: res.data.data.acknowledged ?? 0,
            alerts_count: res.data.data.alerts_count ?? 0,
          })
        }
      } catch (error) {
        console.error(error)
      }
    }

    fetchData()
  }, [unsupported, updateIndex, triggerGetAlertStatuses, dateRange])

  const data = unsupported ? dummyAlertsStatusesWidgetData : widgetData

  const CHART_COLORS = {
    active: "#22C55E",
    frozen: "#475569",
    acknowledged: "#2DB2C8",
  } as const

  const hasNoData = data.active + data.frozen + data.acknowledged === 0

  return (
    <WidgetsLayout title="Alerts Statuses" isFetching={!unsupported && isFetching}>
      <div className={styles.chartWrapper}>
        <BarChart
          showEmptyState
          items={
            hasNoData
              ? []
              : [
                  { color: CHART_COLORS.active, value: data.active },
                  { color: CHART_COLORS.frozen, value: data.frozen },
                  { color: CHART_COLORS.acknowledged, value: data.acknowledged },
                ]
          }
        />
        {!hasNoData && (
          <footer className={styles.footer}>
            <div className={styles.footerItem}>
              <span className={styles.footerNote} style={{ ["--dot-color" as string]: CHART_COLORS.active }}>
                Active
              </span>
              <span className={styles.footerValue}>{data.active}</span>
            </div>
            <div className={styles.footerItem}>
              <span className={styles.footerNote} style={{ ["--dot-color" as string]: CHART_COLORS.frozen }}>
                Frozen
              </span>
              <span className={styles.footerValue}>{data.frozen}</span>
            </div>
            <div className={styles.footerItem}>
              <span className={styles.footerNote} style={{ ["--dot-color" as string]: CHART_COLORS.acknowledged }}>
                Acknowledged
              </span>
              <span className={styles.footerValue}>{data.acknowledged}</span>
            </div>
          </footer>
        )}
      </div>
    </WidgetsLayout>
  )
}
